import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import LiveCard from "@/components/base/LiveCard";
import { LiveStatusBadge, SignalMeter } from "@/components/base/LiveIndicators";
import { liveSessions } from "@/mocks/lives";
import { viewingRules } from "@/mocks/content";
import { SITE } from "@/config/site";
import { cn, formatViewers } from "@/utils/format";

interface CommentItem {
  id: number;
  name: string;
  text: string;
  time: string;
}

const seedComments: CommentItem[] = [
  { id: 1, name: "路过的邻居", text: "画面一直在，没切镜，这点做得挺踏实。", time: "8 分钟前" },
  { id: 2, name: "夜班阿哲", text: "连麦那段听得很清楚，语气就是现场的感觉。", time: "21 分钟前" },
  { id: 3, name: "本地老张", text: "坐标就在隔壁街，和我在楼下看到的一致。", time: "46 分钟前" },
];

export default function LiveDetail() {
  const { id } = useParams();
  const item = liveSessions.find((live) => live.id === id);

  const [soundOn, setSoundOn] = useState(item?.sound ?? true);
  const [liked, setLiked] = useState(false);
  const [shareTip, setShareTip] = useState("");
  const [comments, setComments] = useState<CommentItem[]>(seedComments);
  const [draftName, setDraftName] = useState("");
  const [draftText, setDraftText] = useState("");

  const related = useMemo(() => {
    if (!item) return [];
    return liveSessions
      .filter((live) => live.id !== item.id)
      .sort((a, b) => {
        const sameA = a.category === item.category ? 1 : 0;
        const sameB = b.category === item.category ? 1 : 0;
        if (sameA !== sameB) return sameB - sameA;
        return b.viewers - a.viewers;
      })
      .slice(0, 3);
  }, [item]);

  if (!item) {
    return (
      <section className="flex min-h-[70vh] w-full flex-col items-center justify-center gap-4 px-4 pt-16 text-center md:pt-20">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
          <i className="ri-signal-wifi-off-line text-2xl" aria-hidden="true" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">
          这路信号暂时不可用
        </h1>
        <p className="max-w-md text-sm leading-relaxed text-foreground-600">
          该直播可能已经结束并归档，或者链接有误。你可以回到直播大厅查看当前正在回传的所有现场。
        </p>
        <Link
          to="/live"
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" aria-hidden="true" />
          返回直播大厅
        </Link>
      </section>
    );
  }

  const handleShare = async () => {
    const url = `${SITE.url}/live/${item.id}`;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShareTip("直播链接已复制，可以发给朋友一起看");
      } else {
        setShareTip(url);
      }
    } catch {
      setShareTip("复制失败，请手动复制地址栏链接");
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = draftText.trim();
    if (!text) return;
    setComments((list) => [
      { id: Date.now(), name: draftName.trim() || "匿名网友", text, time: "刚刚" },
      ...list,
    ]);
    setDraftName("");
    setDraftText("");
  };

  return (
    <>
      <Seo
        title={`${item.title} - 爆料直播现场直击`}
        description={`${item.summary.slice(0, 70)} 本场直播位于${item.location}，实时画面信号 ${item.signal}%，${item.sound ? "包含现场连麦声音" : "当前为静音画面"}。`}
        keywords={`${item.title},${item.tags.join(",")},现场实时画面,突发事态直击`}
        path={`/live/${item.id}`}
        image={item.cover}
        imageAlt={`${item.title} 的现场实时画面`}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: item.title,
            url: `${SITE.url}/live/${item.id}`,
            inLanguage: "zh-CN",
            description: item.summary,
            isPartOf: { "@type": "WebSite", url: SITE.url },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "直播大厅", item: `${SITE.url}/live` },
              { "@type": "ListItem", position: 3, name: item.title, item: `${SITE.url}/live/${item.id}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: item.title,
            description: item.summary,
            thumbnailUrl: item.cover,
            uploadDate: "2026-09-10T08:00:00+08:00",
            contentUrl: `${SITE.url}/live/${item.id}`,
            embedUrl: `${SITE.url}/live/${item.id}`,
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
          },
        ]}
      />

      <section className="w-full border-b border-background-200/70 px-4 pb-6 pt-16 md:px-6 md:pb-8 md:pt-20">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "直播大厅", to: "/live" },
            { label: item.category },
            { label: item.title },
          ]}
          className="mt-6"
        />
      </section>

      <section className="w-full bg-background-50 px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <div className="relative aspect-video w-full bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 的现场实时画面`}
                  title={`${item.title} - 现场实时画面`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/10 to-background-950/30" />
                <div className="absolute left-3 top-3 md:left-4 md:top-4">
                  <LiveStatusBadge status={item.status} size="md" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-50/40 bg-background-950/40 text-background-50 backdrop-blur md:h-16 md:w-16">
                    <i className="ri-play-fill text-2xl md:text-3xl" aria-hidden="true" />
                  </span>
                </div>
                <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-2 md:inset-x-4 md:bottom-4">
                  <SignalMeter
                    value={item.signal}
                    sound={soundOn}
                    showLabel
                    className="rounded-full bg-background-950/65 px-3 py-1.5 backdrop-blur"
                  />
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-label text-[11px] text-foreground-800">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatViewers(item.viewers)} 人在看
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-background-200/70 p-3 md:p-4">
                <button
                  type="button"
                  onClick={() => setSoundOn((value) => !value)}
                  aria-pressed={soundOn}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-[13px] transition-colors",
                    soundOn
                      ? "bg-secondary-100/80 text-secondary-900"
                      : "border border-background-300/60 text-foreground-700 hover:border-primary-500/60 hover:text-primary-500"
                  )}
                >
                  <i className={soundOn ? "ri-volume-up-line" : "ri-volume-mute-line"} aria-hidden="true" />
                  {soundOn ? "连麦声音已开" : "连麦声音已关"}
                </button>
                <button
                  type="button"
                  onClick={() => setLiked((value) => !value)}
                  aria-pressed={liked}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-[13px] transition-colors",
                    liked
                      ? "bg-accent-500 text-background-50"
                      : "border border-background-300/60 text-foreground-700 hover:border-primary-500/60 hover:text-primary-500"
                  )}
                >
                  <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                  {liked ? "已关注本场" : "关注本场直播"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-3 py-2 text-[13px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-500"
                >
                  <i className="ri-share-forward-line" aria-hidden="true" />
                  复制直播链接
                </button>
                <span className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap font-label text-[11px] text-foreground-500">
                  <i className="ri-timer-line" aria-hidden="true" />
                  {item.duration}
                </span>
              </div>
              {shareTip && (
                <p className="border-t border-background-200/70 px-3 py-2 text-[12px] text-primary-500 md:px-4">
                  {shareTip}
                </p>
              )}
            </div>

            <header className="mt-6">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-foreground-500">
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-secondary-700">
                  <i className="ri-price-tag-3-line" aria-hidden="true" />
                  {item.category}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-map-pin-line" aria-hidden="true" />
                  {item.location}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-user-voice-line" aria-hidden="true" />
                  {item.host}
                </span>
                <span className="whitespace-nowrap font-label">{item.startAt}</span>
              </div>
              <h1 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-3xl">
                {item.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.summary}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <section className="mt-8 rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-focus-3-line text-primary-500" aria-hidden="true" />
                本场镜头看点
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {item.highlights.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-secondary-500" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-8" aria-labelledby="comments-title">
              <h2
                id="comments-title"
                className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950"
              >
                <i className="ri-chat-3-line text-primary-500" aria-hidden="true" />
                现场讨论 · {comments.length} 条
              </h2>

              <form onSubmit={handleSubmit} className="mt-4 rounded-lg border border-background-200/70 bg-background-100 p-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="comment-name" className="mb-1.5 block text-[12px] text-foreground-500">
                      昵称（可留空）
                    </label>
                    <input
                      id="comment-name"
                      name="nickname"
                      type="text"
                      maxLength={20}
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      placeholder="匿名网友"
                      className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500/70 placeholder:text-foreground-400"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label htmlFor="comment-text" className="mb-1.5 block text-[12px] text-foreground-500">
                      围绕现场事实发言
                    </label>
                    <input
                      id="comment-text-hidden"
                      type="text"
                      name="website_alt"
                      className="field-alt-note"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <input
                      id="comment-text"
                      name="content"
                      type="text"
                      maxLength={120}
                      value={draftText}
                      onChange={(event) => setDraftText(event.target.value)}
                      placeholder="说说你看到或听到的现场情况"
                      className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500/70 placeholder:text-foreground-400"
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[11px] text-foreground-500">
                    请勿发布未经核实的当事人信息与个人信息。
                  </p>
                  <button
                    type="submit"
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-[13px] text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-fill" aria-hidden="true" />
                    发布讨论
                  </button>
                </div>
              </form>

              <ul className="mt-5 flex flex-col gap-4">
                {comments.map((comment) => (
                  <li key={comment.id} className="flex gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100/60 font-heading text-sm font-bold text-primary-500">
                      {comment.name.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-foreground-500">
                        <span className="font-medium text-foreground-900">{comment.name}</span>
                        <span className="whitespace-nowrap">{comment.time}</span>
                      </p>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-700">
                        {comment.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <section className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-error-warning-line text-accent-500" aria-hidden="true" />
                观看须知
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {viewingRules.map((rule) => (
                  <li key={rule} className="flex items-start gap-2.5 text-[12px] leading-relaxed text-foreground-600">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
                    {rule}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-6" aria-labelledby="related-title">
              <h2
                id="related-title"
                className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950"
              >
                <i className="ri-links-line text-primary-500" aria-hidden="true" />
                相关现场推荐
              </h2>
              <div className="mt-4 flex flex-col gap-4">
                {related.map((live) => (
                  <LiveCard key={live.id} item={live} />
                ))}
              </div>
            </section>
          </aside>
        </div>
      </section>
    </>
  );
}